'use client';

import { useEffect, useRef, useState } from 'react';

import { ModalPrinterComparison } from '@monetization/hpaip-components-library';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import ThemeProviderWrapper from '@storefront/app/ThemeProviderWrapper';
import useGetProductsData from '@storefront/hooks/useGetProductsData';
import DataCollection from '@storefront/services/DataCollection';
import { uiEvents } from '@storefront/services/DataCollection/simpleUiEvents';

import {
  getInitialSelection,
  getResponsiveColumnCount,
  getShouldRenderSelect,
  handleFullSpecsAnalytics,
} from './modal-printer-dynamic-comparison-utils';
import visIDStyles from './modal-printer-dynamic-comparison-visid.module.scss';
import {
  featuresKeys,
  printersFeaturesList,
} from './modal-printer-dynamic-comparison.features';
import legacyStyles from './modal-printer-dynamic-comparison.module.scss';

interface ModalDynamicComparisonProps {
  noFirstText?: boolean;
  secondText?: string;
  underlineLink?: boolean;
  enableVisIDStyles?: boolean;
}

export function ModalDynamicComparison({
  noFirstText,
  secondText,
  underlineLink,
  enableVisIDStyles = false,
}: Readonly<ModalDynamicComparisonProps>) {
  const { productsOptions, productsInfo, multipleProduct } =
    useGetProductsData();

  const styles = enableVisIDStyles ? visIDStyles : legacyStyles;
  const productConfig = sessionStorage.getItem('product-configuration');
  const config = productConfig ? JSON.parse(productConfig) : undefined;
  const activeSKU = config?.activeSku || (null as unknown as string);

  const buttonRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation();

  const [showModal, setShowModal] = useState(false);

  const productsOptionsLength = productsOptions ? productsOptions.length : 0;
  const responsiveColumnCount = getResponsiveColumnCount(productsOptionsLength);
  const [columnsNumber, setColumnsNumber] = useState(responsiveColumnCount);
  const initialSelection = getInitialSelection(
    activeSKU,
    productsOptions,
    columnsNumber
  );

  const featuresList = printersFeaturesList(multipleProduct);

  useEffect(() => {
    const resizeHandler = () => {
      const currentResponsiveColumnCount = getResponsiveColumnCount(
        productsOptionsLength
      );
      setColumnsNumber(currentResponsiveColumnCount);
    };
    resizeHandler();
    window.addEventListener('resize', resizeHandler);
    return () => window.removeEventListener('resize', resizeHandler);
  }, []);

  useEffect(() => {
    if (showModal) {
      DataCollection.sendEvent({
        ...uiEvents.comparePrintersModalDisplayed,
      });
    }
  }, [showModal]);

  useEffect(() => {
    const handlePopState = () => setShowModal(false);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const shouldRenderSelect = getShouldRenderSelect(
    columnsNumber,
    productsOptions
  );
  if (!productsOptions || productsOptions.length === 0) return null;

  return (
    <ThemeProviderWrapper shape="round">
      <ModalPrinterComparison
        isOpen={showModal}
        initialSelection={initialSelection}
        productsOptions={productsOptions}
        labels={{
          title: shouldRenderSelect ? t('modals.modalComparison.title') : '',
          fullSpecs: t('modals.modalComparison.seeSpecs'),
        }}
        productsInfo={productsInfo}
        featuresData={{
          featuresKeys: featuresKeys,
          printersFeaturesList: featuresList,
        }}
        setOpenModal={setShowModal}
        onAnalytics={handleFullSpecsAnalytics}
      />
      <div className={styles['modalAction']}>
        {!noFirstText && (
          <span
            className={classNames(styles['modalText'], {
              'caption-regular': enableVisIDStyles,
            })}
          >
            {t('modals.modalComparison.decide')}
          </span>
        )}
        <input
          id="open-dynamic-modal-printer-comparison"
          ref={buttonRef}
          tabIndex={0}
          data-testid="showModalButton"
          type="button"
          className={classNames(
            styles['modal-button'],
            underlineLink && styles['underlinedLink'],
            { 'caption-regular': enableVisIDStyles }
          )}
          value={secondText || t('modals.modalComparison.modalName')}
          onClick={async () => {
            await DataCollection.sendEvent({
              ...uiEvents.printerConfigViewMore,
              screenMode: 'ChooseYourPrinter',
              controlName: 'ComparePrinters',
            });
            setShowModal(true);
          }}
        />
      </div>
    </ThemeProviderWrapper>
  );
}

export default ModalDynamicComparison;
